import React, { useEffect, useMemo, useCallback} from "react";
import PropTypes from "prop-types";
import {TaxIdentity as TaxIdentityApi} from "../../../api/services/next";
import {Modal, Segment, Progress, Button} from "semantic-ui-react";
import useBoolean from "../../../hooks/useBoolean";
import useSafeState from "../../../hooks/useSafeState";
import { useTextResources } from '../../../language';

const HealthCheckRunResult = {
    Success: "success",
    Failed: "failed",
};

const TokenExpirationCheckPopup = ({
    open,
    onClose,
    taxIdentityIds,
}) => {
    const text = useTextResources();
    const [runCheckTasks, setRunCheckTasks] = useSafeState({});
    const {
        value: isCheckRun,
        setFalse: stopCheckRun,
        toggle: toggleCheckRun,
    } = useBoolean(false);

    useEffect(() => {
        setRunCheckTasks(Object.fromEntries(taxIdentityIds.map(id => [id, false])));
        return () => {
            setRunCheckTasks({});
            stopCheckRun();
        };
    }, [taxIdentityIds, setRunCheckTasks, stopCheckRun]);

    const runCheck = useCallback(async (taxIdentityId) => {
        let result = undefined;
        try {
            await TaxIdentityApi.TaxAuthorityHealthCheck({Id: taxIdentityId});
            result = HealthCheckRunResult.Success;
            
        } catch (err) {
            result = HealthCheckRunResult.Failed;
        }

        setRunCheckTasks((prevState) => ({
            ...prevState,
            [taxIdentityId]: result,
        }));
    }, [setRunCheckTasks]);

    useEffect(() => {
        const taxIdentityIdToCheck = Object.keys(runCheckTasks).find((taxIdentityId) => !runCheckTasks[taxIdentityId]); 
        if (isCheckRun && taxIdentityIdToCheck) {
            runCheck(taxIdentityIdToCheck);
        } else {
            stopCheckRun();
        }
    }, [isCheckRun, stopCheckRun, runCheckTasks, runCheck]);

    const {progressPercent, completedChecks, totalChecks, successfulChecks, failedChecks } = useMemo(() => {
        const keys = Object.keys(runCheckTasks);
        const successfulChecks = keys.filter((taxIdentityId) => Boolean(runCheckTasks[taxIdentityId] === HealthCheckRunResult.Success)).length;
        const failedChecks = keys.filter((taxIdentityId) => Boolean(runCheckTasks[taxIdentityId] === HealthCheckRunResult.Failed)).length;
        const completedChecks =  successfulChecks + failedChecks;
        const totalChecks = keys.length;
        const progressPercent = totalChecks > 0 && completedChecks < totalChecks ? completedChecks / totalChecks * 100 : 100;
        return {
            progressPercent,
            completedChecks,
            totalChecks,
            successfulChecks,
            failedChecks,
        };
    }, [runCheckTasks]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            dimmer
            centered
            closeOnEscape
        >
            <Modal.Header content={text("RunTokenExpirationChecks")} />
            <Modal.Content>
                <Segment>
                    <Progress
                        percent={progressPercent}
                        attached='bottom'
                        active={isCheckRun}
                        indicating
                    />
                    <p>{`${text("Completed")} / ${text("Total")}: ${completedChecks} / ${totalChecks}`}</p>
                    <p>{`${text("Success")} / ${text("Failed")} : ${successfulChecks} / ${failedChecks}`}</p>
                </Segment>
            </Modal.Content>
            <Modal.Actions>
                <Button negative onClick={onClose}>
                    {isCheckRun ? text("StopAndClose") : text("Close")}
                </Button>
                <Button positive onClick={toggleCheckRun}>
                    {isCheckRun ? text("Stop") : text("Run")}
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

TokenExpirationCheckPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    taxIdentityIds: PropTypes.arrayOf(PropTypes.number),
};

TokenExpirationCheckPopup.defaultProps = {
    taxIdentityIds: [],
};

export default TokenExpirationCheckPopup;
